import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Card } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { DateField } from "@/shared/ui/date-field";
import { useLanguage } from "@/shared/hooks/use-language";
import { endOfDayInFilterZone } from "@/shared/lib/date-utils";
import { Mail, Plus, X } from "lucide-react";
import { ScopeContextStorage } from "@/shared/lib/scope-storage.ts";
import type { ScopeContext } from "@/features/auth";
import { getOwnAccesses } from "@/features/auth/api/user.ts";
import { useUserProfile } from "@/features/auth/model/use-user-profile.ts";
import {
  grantUserAccesses,
  cancelUserAccesses,
  type UserAccess,
  type AccessRole,
  type UserAccessFilters,
  type UserAccessStatus,
} from "@/features/auth/api/user-access.ts";
import { useAccessListing } from "@/features/auth/model/use-access-listing.ts";
import { AccessCard } from "@/features/auth/ui/AccessCard.tsx";
import { AccessList } from "@/features/auth/ui/AccessList.tsx";
import { deriveHolderAccess } from "@/features/auth/model/holder-access.ts";
import { getRoles, type Role } from "@/features/auth/api/role.ts";
import { sendUserInvite } from "@/features/auth/api/user-invite.ts";
import { DebugLogger } from "@/shared/lib/debug.ts";
import { parseBackendErrors } from "@/shared/lib/backend-errors.ts";
import { isUnreadableResponseError } from "@/shared/lib/response-shape.ts";
import { exitToLogin } from "@/shared/lib/session-exit.ts";
import { useToast } from "@/shared/hooks/use-toast";
import { UserCardSkeleton } from "@/shared/components/UserCardSkeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";

// b-cell-account reports the failing field under auth's own body path on the writes it
// delegates, so both spellings map to the same control.
const ACCESS_FIELD_MAP: Record<string, string> = {
  "accesses.user_id": "user",
  "accesses.role_ids": "roles",
  "accesses.id": "access",
  "accesses.status": "access",
  "bindings.role_ids": "roles",
  "bindings.id": "access",
};

type ViewMode = "account" | "holder";

// Persons and Integrations answer "who has access right now", so both pin status to active
// and differ only by user type. Access History answers "what happened", so it pins nothing
// and hands the three filters to the user instead.
type AccessTab = "persons" | "integrations" | "history";

// DateField emits at most once per completed date, and once for a partial entry, not once
// per keystroke (see its own `onChange` doc comment). A fresh date typed into an empty
// field is one flush on its own -- nothing to fold. Editing a date the field already holds
// costs one more: as soon as the entry stops being a complete date, the field reports `""`
// -- by design, since a stale bound must never be applied while the entry is mid-edit --
// and the finished replacement emits its own ISO after. Those two flushes are not folded
// into one; the empty one is the field correctly retracting a bound it can no longer stand
// behind. What this debounce does fold is a burst of changes across controls that land in
// the same edit -- the status select changing alongside a date, or Clear filters resetting
// all three -- so they reach the backend as the single request they were meant to produce
// together, not as one per control.
const FILTER_DEBOUNCE_MS = 300;

const ACCESS_TABS: AccessTab[] = ["persons", "integrations", "history"];

// Which scope is listed is the point of the toggle above, and the styled-button state was
// the only thing still saying so once the old scope paragraph went. On an access-control
// screen that is the fact worth spelling out, so the tab description carries it -- and each
// panel points at it rather than merely sitting next to it.
const TAB_DESCRIPTION_KEY: Record<AccessTab, Record<ViewMode, string>> = {
  persons: { account: "access.personsDesc", holder: "access.holderPersonsDesc" },
  integrations: {
    account: "access.integrationsDesc",
    holder: "access.holderIntegrationsDesc",
  },
  history: { account: "access.historyDesc", holder: "access.holderHistoryDesc" },
};

const TAB_DESCRIPTION_ID = "access-tab-description";

// Router state is whatever the previous screen put there, so it is read as a request rather
// than as a value: anything unrecognised falls back to the screen's own default, which is
// also what a direct visit to /console/access gets.
const requestedTabFrom = (value: unknown): AccessTab =>
  ACCESS_TABS.includes(value as AccessTab) ? (value as AccessTab) : "persons";

interface HistoryFilters {
  status: UserAccessStatus | "";
  after: string;
  before: string;
}

const EMPTY_HISTORY_FILTERS: HistoryFilters = { status: "", after: "", before: "" };

// Every filter is a server-side one. Filtering a page the backend already narrowed would
// answer off whatever happens to be loaded, which is the same trap the search box drains
// the listing to avoid -- and a date range is far likelier than a name to fall outside the
// first page.
//
// The user picks days; the backend bounds instants. `after` needs no translation because
// midnight is already the right lower edge for "from" -- do not make this symmetric. `before`
// does: a bare date is read as *midnight*, so sending it raw makes "until 13 Aug" mean "up to
// the first instant of 13 Aug", and an equal from/until pair returns nothing at all.
const filtersFor = (tab: AccessTab, history: HistoryFilters): UserAccessFilters => {
  if (tab === "persons") return { status: "active", user_type: "person" };
  if (tab === "integrations") return { status: "active", user_type: "integration" };
  // `null` means the text was not a real calendar date. Dropping the bound is the safe
  // reading: the date field cannot produce one, and sending a bound we could not compose
  // would turn the filter into an error card.
  const historyBefore = history.before ? endOfDayInFilterZone(history.before) : null;
  return {
    ...(history.status ? { status: history.status } : {}),
    ...(history.after ? { after: history.after } : {}),
    ...(historyBefore ? { before: historyBefore } : {}),
  };
};

const AccessManagement = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const sessionId = location.state?.sessionId || ScopeContextStorage.getCurrentSessionId();
  const passedScopeContext = location.state?.scopeContext;

  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("account");
  const [activeTab, setActiveTab] = useState<AccessTab>(
    requestedTabFrom(location.state?.accessTab)
  );
  const [historyFilters, setHistoryFilters] = useState<HistoryFilters>(EMPTY_HISTORY_FILTERS);
  // What the controls hold vs what has been queried. Clearing sets both, so Clear filters
  // stays immediate; typing only moves the first until the debounce catches up.
  const [appliedHistoryFilters, setAppliedHistoryFilters] =
    useState<HistoryFilters>(EMPTY_HISTORY_FILTERS);
  // Bumped by "Clear filters" and folded into both DateFields' `key`. `value` alone cannot
  // force an unparsed entry off the screen when the field already reported the same "" the
  // clear is about to set — see `DateField`'s own doc comment. A fresh `key` remounts the
  // field instead of relying on a `value` change that, in that one case, never arrives.
  const [historyResetCount, setHistoryResetCount] = useState(0);
  const [hasHolderAccess, setHasHolderAccess] = useState(false);
  // A handed-over holder scope is applied only once holder access is known to exist, never
  // as the initial value. Starting in the Organization view would race the probe that
  // decides whether that view exists at all: the listing behind it has not been asked for
  // yet, so the screen would be claiming a scope it cannot yet show -- and the guard below
  // would bounce it straight back to Account before the probe answered.
  const requestedHolderView = location.state?.accessViewMode === "holder";
  const appliedRequestedView = useRef(false);
  // The page-level skeleton replaces the whole screen, tab strip and filter card included.
  // That is right for the first paint and wrong for every refetch after it: a tab click or a
  // filter edit would unmount the control that triggered it and drop focus. Once the screen
  // has painted a listing, loading degrades inline through listSection's own branch instead.
  const [hasPainted, setHasPainted] = useState(false);

  const [inviteEmail, setInviteEmail] = useState("");
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [showEditRolesModal, setShowEditRolesModal] = useState(false);
  const [editingAccess, setEditingAccess] = useState<UserAccess | null>(null);
  const [editedRoles, setEditedRoles] = useState<Array<{ id: string; name: string }>>([]);
  const [editError, setEditError] = useState<string | null>(null);
  const [isUpdatingRoles, setIsUpdatingRoles] = useState(false);

  const [revokingAccess, setRevokingAccess] = useState<UserAccess | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [confirmSelfRevoke, setConfirmSelfRevoke] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);

  const { user: currentUser } = useUserProfile();
  const currentUserId = currentUser?.id ?? null;
  const identityResolved = currentUserId !== null;
  const { toast } = useToast();

  useEffect(() => {
    const timer = setTimeout(() => setAppliedHistoryFilters(historyFilters), FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [historyFilters]);

  // Memoised because the listings and their effects depend on it: a fresh object every
  // render would refetch on every render instead of on every filter change.
  const filters = useMemo(
    () => filtersFor(activeTab, appliedHistoryFilters),
    [activeTab, appliedHistoryFilters],
  );

  // One per scope, because both load concurrently: the holder listing is what decides
  // whether the Organization toggle exists at all, so it is fetched before the user can ask
  // for it, and switching views must not refetch the view being left.
  const accountListing = useAccessListing({
    sessionId: sessionId || "",
    filters,
    initialLoading: true,
  });
  const holderListing = useAccessListing({ sessionId: sessionId || "", filters });

  // Two booleans, deliberately. The Clear control reflects what the user has typed, so it
  // reads the live state; the empty-state copy is a claim about the listing on screen, so it
  // reads what was actually queried -- otherwise it blames filters mid-debounce.
  const hasHistoryFilters = Boolean(
    historyFilters.status || historyFilters.after || historyFilters.before
  );
  const hasAppliedHistoryFilters = Boolean(
    appliedHistoryFilters.status || appliedHistoryFilters.after || appliedHistoryFilters.before
  );

  const currentContext = () =>
    passedScopeContext || ScopeContextStorage.getScopeContext(sessionId || "");

  const scopeForView = (context: ScopeContext, mode: ViewMode): ScopeContext => {
    if (mode === "account") {
      if (!context.account) throw new Error(t("access.noAccountScope"));
      return { scopeId: context.account.id, scopeType: "account" };
    }
    const holderId = context.accountHolder?.id || context.account?.holder_id;
    if (!holderId) throw new Error(t("access.noHolderScope"));
    return { scopeId: holderId, scopeType: "holder" };
  };

  const readableError = (err: unknown, fallback: string) => {
    if (isUnreadableResponseError(err)) return t("errors.unreadableResponse");
    return err instanceof Error ? err.message : fallback;
  };

  useEffect(() => {
    if (!sessionId) {
      DebugLogger.error("No session ID found", {
        locationState: location.state,
        pathname: location.pathname,
      });
      setError(t("access.noSession"));
      accountListing.clear();
      return;
    }

    const contextToUse = passedScopeContext || ScopeContextStorage.getScopeContext(sessionId);

    if (!contextToUse) {
      DebugLogger.error("No scope context found", { sessionId });
      setError(t("access.noScope"));
      accountListing.clear();
      return;
    }

    // Same guard as the Integrations screen: an account without an id is a scope nothing
    // can be requested against, so it is rejected here rather than reaching the listings.
    if (!contextToUse.account?.id) {
      DebugLogger.error("Incomplete scope context - no usable account", { contextToUse });
      setError(t("access.incompleteScope"));
      accountListing.clear();
      return;
    }

    fetchAccesses(contextToUse, sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, passedScopeContext, filters]);

  // The invite form's roles do not vary with the tab, so this stays out of the effect above
  // -- which now re-runs on every filter change.
  // It keeps that effect's context guard: on the noScope / incompleteScope screens there is
  // no invite form to fill, and a failed load would raise a destructive toast over the error
  // card -- the one screen where roles are irrelevant.
  useEffect(() => {
    if (!sessionId) return;
    const contextToUse = passedScopeContext || ScopeContextStorage.getScopeContext(sessionId);
    if (!contextToUse?.account) return;
    loadRoles(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, passedScopeContext]);

  // The Organization view is reachable only through the toggle pair, and the toggles render
  // behind hasHolderAccess. Revoking your own holder access unmounts them, so without this
  // the user is left looking at holder data with no control that can leave it.
  useEffect(() => {
    if (!hasHolderAccess && viewMode === "holder") setViewMode("account");
  }, [hasHolderAccess, viewMode]);

  // Once, on the mount that was handed the scope. Latched rather than left to the
  // dependencies, so a later loss and re-grant of holder access cannot drag the user back
  // to a view they have since left by hand.
  //
  // The latch is consumed on the FIRST holder load's terminal outcome, whichever it is --
  // success, error, denied, or no holder id -- not only on success. Gating the consuming
  // assignment on `hasHolderAccess` (as this used to) left the denied and no-holder-id
  // outcomes unable to ever set `appliedRequestedView.current`, since those leave
  // `hasHolderAccess` false forever on the mount that reached them; if holder access were
  // ever granted later in the same session (a role change, a fresh probe on some later
  // reload), the guard would then pass for the first time and jump the user into
  // Organization off router state from a navigation minutes in the past. So the guard below
  // depends on `holderListing.state.hasSettled` alone, and `hasHolderAccess` moves into the
  // decision of *what to do once consumed* instead of gating consumption itself.
  //
  // `hasHolderAccess` alone was never enough to decide the switch either way:
  // `loadHolderAccesses` sets it true in its catch for every error that is not
  // invalid-permission, so the flag can mean "the toggle exists" while the listing behind
  // it holds an error, and switching onto it would walk the user into that error card
  // rather than leaving them beside it -- hence the `!error` check.
  //
  // `hasSettled` (not `hasAnswered`) is what makes this reachable at all on an error settle:
  // `hasAnswered` stays false on a failed load, which is indistinguishable from never having
  // asked, so a latch gated on it waited past every failed first load and fired later on
  // whatever reload happened to succeed -- switching the user's scope out from under them,
  // with no relation to the hand-off that requested it. `hasSettled` is true on every
  // terminal outcome of the load, error included, so the latch consumes exactly once, on
  // the load the hand-off itself triggered.
  //
  // The read below assumes `hasSettled` (written inside `holderListing.loadFirstPage`) and
  // `hasHolderAccess` (written here in `loadHolderAccesses`, one `await` after that call)
  // land in the same React 18 commit -- both branches of `loadHolderAccesses` set the local
  // flag in the statement right after `await holderListing.loadFirstPage(...)` settles, so
  // the two writes are order-coupled rather than independently timed. The latch reads them
  // together in one effect run, which only holds because of that coupling: a future
  // `flushSync` around either write, or splitting them across renders, would let this effect
  // observe `hasSettled: true` with `hasHolderAccess` still stale-false and silently skip
  // the switch -- and since the latch is consumed on that same run, there is no second
  // chance once `hasHolderAccess` catches up.
  useEffect(() => {
    if (appliedRequestedView.current || !requestedHolderView) return;
    if (!holderListing.state.hasSettled) return;
    appliedRequestedView.current = true;
    if (hasHolderAccess && !holderListing.state.error) setViewMode("holder");
  }, [requestedHolderView, hasHolderAccess, holderListing.state.hasSettled,
      holderListing.state.error]);

  const loadRoles = async (sessionId: string) => {
    try {
      setIsLoadingRoles(true);
      const rolesResponse = await getRoles(sessionId);
      const selectableRoles = (rolesResponse.roles || []).filter(
        (role: Role) => role.type === "default" || role.type === "custom"
      );
      setRoles(selectableRoles);
    } catch (err) {
      DebugLogger.error("Failed to load roles", {
        error: err instanceof Error ? err.message : "Unknown error",
      });
      toast({
        title: t("access.error"),
        description: readableError(err, "Failed to load roles"),
        variant: "destructive",
      });
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail || !selectedRoleId) {
      toast({
        title: t("access.requiredFields"),
        description: t("access.fillAllFields"),
        variant: "destructive",
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      toast({
        title: t("access.invalidEmail"),
        description: t("access.invalidEmailDesc"),
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSendingInvite(true);

      const contextToUse = currentContext();
      const scopeContext = contextToUse?.account
        ? { scopeId: contextToUse.account.id, scopeType: "account" as const }
        : contextToUse?.accountHolder
        ? { scopeId: contextToUse.accountHolder.id, scopeType: "holder" as const }
        : undefined;

      const response = await sendUserInvite(
        sessionId,
        [{ user_email: inviteEmail, role_ids: [selectedRoleId] }],
        scopeContext
      );

      setSuccessMessage(response.message || t("access.inviteSent"));
      setShowSuccessDialog(true);

      setInviteEmail("");
      setSelectedRoleId("");

      if (contextToUse) fetchAccesses(contextToUse, sessionId);
    } catch (err) {
      DebugLogger.error("Failed to send invite", {
        error: err instanceof Error ? err.message : "Unknown error",
      });
      toast({
        title: t("access.error"),
        description: readableError(err, "Failed to send invitation"),
        variant: "destructive",
      });
    } finally {
      setIsSendingInvite(false);
    }
  };

  const fetchAccesses = async (context: ScopeContext, sessionId: string) => {
    if (!context.account) {
      setError(t("access.accountRequired"));
      accountListing.clear();
      return;
    }

    setError(null);
    // The full-page skeleton is replaced once the account listing has answered either way:
    // from here on, loading degrades inline through AccessList rather than unmounting the
    // controls that trigger it.
    accountListing
      .loadFirstPage(scopeForView(context, "account"))
      .catch(() => {
        // The listing has already put the failure on the account list; the rethrow exists
        // for loadHolderAccesses, which inspects err.code.
      })
      .finally(() => setHasPainted(true));

    const holderId = context.accountHolder?.id || context.account.holder_id;

    if (!holderId) {
      DebugLogger.warn("No holder ID available, skipping holder-scoped accesses", {
        account: context.account,
      });
      holderListing.clear();
      setHasHolderAccess(false);
      return;
    }

    loadHolderAccesses(sessionId, holderId);
  };

  const loadHolderAccesses = async (sessionId: string, holderId: string) => {
    const holderScope: ScopeContext = { scopeId: holderId, scopeType: "holder" };
    holderListing.beginLoading();
    try {
      let holderAccess: ReturnType<typeof deriveHolderAccess> = "undetermined";
      try {
        const own = await getOwnAccesses(sessionId, holderScope);
        holderAccess = deriveHolderAccess(own.accesses, Boolean(own.cursor), holderId);
      } catch (err) {
        DebugLogger.warn("Could not resolve own accesses, probing the holder listing instead", {
          error: err instanceof Error ? err.message : "Unknown error",
        });
      }

      if (holderAccess === "denied") {
        holderListing.clear();
        setHasHolderAccess(false);
        return;
      }

      await holderListing.loadFirstPage(holderScope);
      setHasHolderAccess(true);
    } catch (err) {
      const errorCode = (err as { code?: string })?.code;
      if (errorCode === "invalid-permission") {
        DebugLogger.debug("Holder-scoped accesses unavailable for this session");
        holderListing.clear();
        setHasHolderAccess(false);
        return;
      }
      setHasHolderAccess(true);
    }
  };

  const currentList = viewMode === "account" ? accountListing : holderListing;
  const revokingSelf = Boolean(
    revokingAccess && identityResolved && currentUserId
    && revokingAccess.user_id === currentUserId
  );
  // An unresolved identity cannot rule the row out, and a spurious checkbox costs one click
  // while a missing one costs the caller their own access.
  const revokingMaybeSelf = Boolean(revokingAccess && !identityResolved);

  // Both close paths run this: Cancel closes by flipping the controlled prop, which does not
  // fire onOpenChange, so an acknowledgement ticked in an abandoned dialog used to survive
  // into the next one.
  const closeRevokeDialog = () => {
    setRevokingAccess(null);
    setRevokeError(null);
    setConfirmSelfRevoke(false);
  };

  const refreshCurrentView = () => {
    const contextToUse = currentContext();
    if (contextToUse) fetchAccesses(contextToUse, sessionId || "");
  };

  // "Nobody has access" is a different claim per tab, and saying the account has no users
  // while the Integrations tab is open would be wrong on both counts.
  const emptyListingKey = () => {
    if (activeTab === "history") {
      // The filtered claim is scope-neutral and correctly so -- it is about the filters, not
      // about the account. The unfiltered one is a claim about a scope, so it names which,
      // like the other two tabs and like the tab descriptions.
      if (hasAppliedHistoryFilters) return "access.noHistory";
      return viewMode === "account" ? "access.noAnyAccess" : "access.noHolderAnyAccess";
    }
    if (activeTab === "integrations") {
      return viewMode === "account" ? "access.noIntegrations" : "access.noHolderIntegrations";
    }
    return viewMode === "account" ? "access.noPersons" : "access.noHolderPersons";
  };

  const lockedRolesOf = (access: UserAccess): AccessRole[] =>
    access.roles.filter(role => role.type === "automatic");

  const handleEditRoles = (access: UserAccess) => {
    setEditingAccess(access);
    setEditedRoles(
      access.roles
        .filter(role => role.type !== "automatic")
        .map(role => ({ id: role.id, name: role.display_name || role.name }))
    );
    setEditError(null);
    setShowEditRolesModal(true);
  };

  const handleUpdateRoles = async () => {
    if (!editingAccess) return;

    const roleIds = editedRoles.filter(role => role.id).map(role => role.id);
    if (roleIds.length === 0) {
      setEditError(t("access.atLeastOneProfile"));
      return;
    }

    try {
      setIsUpdatingRoles(true);
      setEditError(null);

      const contextToUse = currentContext();
      if (!contextToUse) throw new Error(t("access.noScopeContext"));

      await grantUserAccesses(sessionId || "", scopeForView(contextToUse, viewMode), [
        { user_id: editingAccess.user_id, role_ids: roleIds },
      ]);

      toast({
        title: t("access.userRolesUpdated"),
        description: t("access.userRolesUpdatedDesc"),
      });

      setShowEditRolesModal(false);
      setEditingAccess(null);
      setEditedRoles([]);
      refreshCurrentView();
    } catch (err) {
      DebugLogger.error("Failed to update access roles", {
        error: err instanceof Error ? err.message : "Unknown error",
      });
      const { fieldErrors, generalError } = parseBackendErrors(err, ACCESS_FIELD_MAP);
      setEditError(
        fieldErrors.roles || fieldErrors.user || generalError || t("access.updateRolesFailed")
      );
    } finally {
      setIsUpdatingRoles(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokingAccess) return;

    try {
      setIsRevoking(true);
      setRevokeError(null);

      const contextToUse = currentContext();
      if (!contextToUse) throw new Error(t("access.noScopeContext"));

      await cancelUserAccesses(sessionId || "", scopeForView(contextToUse, viewMode), [
        revokingAccess.id,
      ]);

      toast({
        title: t("access.accessRevoked"),
        description: t("access.accessRevokedDesc"),
      });

      closeRevokeDialog();
      refreshCurrentView();
    } catch (err) {
      DebugLogger.error("Failed to revoke access", {
        error: err instanceof Error ? err.message : "Unknown error",
      });
      const { fieldErrors, generalError } = parseBackendErrors(err, ACCESS_FIELD_MAP);
      setRevokeError(fieldErrors.access || generalError || t("access.revokeFailed"));
    } finally {
      setIsRevoking(false);
    }
  };

  const handleAddRole = () => setEditedRoles([...editedRoles, { id: "", name: "" }]);

  const handleRemoveRole = (index: number) =>
    setEditedRoles(editedRoles.filter((_, position) => position !== index));

  const handleRoleChange = (index: number, roleId: string) => {
    const updatedRoles = [...editedRoles];
    const selectedRole = roles.find(role => role.id === roleId);
    updatedRoles[index] = selectedRole
      ? { id: selectedRole.id, name: selectedRole.display_name || selectedRole.name }
      : { id: "", name: "" };
    setEditedRoles(updatedRoles);
  };

  const getAvailableRoles = (currentIndex: number) => {
    const selectedRoleIds = editedRoles
      .filter((role, index) => index !== currentIndex && role.id)
      .map(role => role.id);
    return roles.filter(role => !selectedRoleIds.includes(role.id));
  };

  // A role the access carries but the picker does not offer still gets its own option, so a
  // row never renders as "no profile" while holding one.
  const getRolesForSelect = (index: number): Array<{ id: string; name: string; display_name?: string }> => {
    const currentRole = editedRoles[index];
    const availableRoles = getAvailableRoles(index);

    if (currentRole?.id && !availableRoles.find(role => role.id === currentRole.id)) {
      const fullCurrentRole = roles.find(role => role.id === currentRole.id);
      return [fullCurrentRole ?? currentRole, ...availableRoles];
    }

    return availableRoles;
  };

  const editingLockedRoles = editingAccess ? lockedRolesOf(editingAccess) : [];
  const isSystemManagedAccess = editingLockedRoles.length > 0;
  const accountName = currentContext()?.account?.name || "";

  if (accountListing.state.isLoading && !hasPainted && viewMode === "account" && !error) {
    return (
      <main className="flex-1 p-4 md:p-8 animate-fadeIn">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-foreground mb-2">{t("access.title")}</h1>
              <p className="text-muted-foreground">{t("access.subtitle")}</p>
            </div>
          </div>

          <Card className="bg-card border-border p-6 mb-6">
            <div className="h-6 w-32 bg-muted rounded mb-4 animate-pulse" />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="h-10 w-full bg-muted rounded md:col-span-2 animate-pulse" />
              <div className="h-10 w-full bg-muted rounded animate-pulse" />
              <div className="h-10 w-full bg-muted rounded animate-pulse" />
            </div>
          </Card>

          <div className="mb-6">
            <div className="h-10 w-full bg-muted rounded animate-pulse" />
          </div>

          <div className="space-y-4">
            <UserCardSkeleton />
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="flex-1 p-4 md:p-8 animate-fadeIn">
        <div className="max-w-7xl mx-auto">
          <Card className="bg-destructive/10 border-destructive/20 p-6">
            <p className="text-destructive">
              <strong>{t("access.error")}</strong> {error}
            </p>
            <Button onClick={exitToLogin} className="mt-4">
              {t("access.signInAgain")}
            </Button>
          </Card>
        </div>
      </main>
    );
  }

  // One list, rendered under whichever tab is open. The tab does not change how the
  // listing behaves -- only which filters it was fetched with -- so each tab renders one
  // AccessList rather than its own copy of the paging, drain and truncation machinery.
  const listSection = (
    <AccessList
      state={currentList.state}
      emptyMessage={t(emptyListingKey())}
      onRetry={refreshCurrentView}
      onLoadMore={currentList.loadMore}
      onRetryLoadMore={currentList.retryLoadMore}
      onContinue={currentList.continuePastCap}
      renderCard={access => (
        <AccessCard
          key={access.id}
          access={access}
          isSelf={identityResolved && access.user_id === currentUserId}
          onEditRoles={handleEditRoles}
          onRevoke={setRevokingAccess}
        />
      )}
    />
  );

  return (
    <>
      <main className="flex-1 p-4 md:p-8 animate-fadeIn">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-foreground mb-2">{t("access.title")}</h1>
              <p className="text-muted-foreground">{t("access.subtitle")}</p>
            </div>
          </div>

          <Card className="bg-card border-border p-6 mb-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">{t("access.newInvite")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Input
                type="email"
                placeholder={t("access.emailPlaceholder")}
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                className="bg-background border-border md:col-span-2"
              />

              <select
                value={selectedRoleId}
                onChange={e => setSelectedRoleId(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isLoadingRoles}
              >
                <option value="">
                  {isLoadingRoles ? t("access.loadingRoles") : t("access.selectRole")}
                </option>
                {roles.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.display_name || role.name}
                  </option>
                ))}
              </select>

              <Button
                onClick={handleSendInvite}
                disabled={isSendingInvite || !inviteEmail || !selectedRoleId}
                className="bg-primary hover:bg-primary/90 gap-2"
              >
                <Mail className="w-4 h-4" />
                {isSendingInvite ? t("access.sending") : t("access.sendInvite")}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-3" data-testid="access-invite-scope">
              {t("access.inviteScopeNote")} {accountName}
            </p>
          </Card>

          <div className="mb-6">
            {hasHolderAccess && (
              <div className="flex items-center gap-2 mb-4">
                <Button
                  onClick={() => setViewMode("account")}
                  variant={viewMode === "account" ? "default" : "outline"}
                  className={viewMode === "account" ? "bg-primary text-primary-foreground" : ""}
                  data-testid="access-account-toggle"
                >
                  {t("access.accountUsers")}
                </Button>
                <Button
                  onClick={() => setViewMode("holder")}
                  variant={viewMode === "holder" ? "default" : "outline"}
                  className={viewMode === "holder" ? "bg-primary text-primary-foreground" : ""}
                  data-testid="access-holder-toggle"
                >
                  {t("access.holderUsers")}
                </Button>
              </div>
            )}
          </div>

          <Tabs
            value={activeTab}
            onValueChange={value => setActiveTab(value as AccessTab)}
          >
            {/* Stopgap: the base TabsList is h-10 inline-flex with no wrap, so a third
                trigger clips at 375px in pt. Fixed here rather than in the shared component
                to keep three unrelated screens out of this change; the shared component is
                where it belongs once those screens can be checked together. */}
            <TabsList className="mb-4 flex-wrap h-auto">
              <TabsTrigger value="persons" data-testid="access-tab-persons">
                {t("access.tabPersons")}
              </TabsTrigger>
              <TabsTrigger value="integrations" data-testid="access-tab-integrations">
                {t("access.tabIntegrations")}
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="access-tab-history">
                {t("access.tabHistory")}
              </TabsTrigger>
            </TabsList>

            <p
              id={TAB_DESCRIPTION_ID}
              className="text-sm text-muted-foreground mb-4"
              data-testid="access-tab-description"
            >
              {t(TAB_DESCRIPTION_KEY[activeTab][viewMode])}
            </p>

            <TabsContent value="persons" aria-describedby={TAB_DESCRIPTION_ID}>
              {listSection}
            </TabsContent>
            <TabsContent value="integrations" aria-describedby={TAB_DESCRIPTION_ID}>
              {listSection}
            </TabsContent>
            <TabsContent value="history" aria-describedby={TAB_DESCRIPTION_ID}>
              <Card className="bg-card border-border p-6 mb-6">
                <h2 className="text-lg font-semibold text-foreground mb-4">
                  {t("access.historyFilters")}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="access-history-status">{t("access.accessStatus")}</Label>
                    <select
                      id="access-history-status"
                      value={historyFilters.status}
                      onChange={e => setHistoryFilters({
                        ...historyFilters,
                        status: e.target.value as UserAccessStatus | "",
                      })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      data-testid="access-history-status"
                    >
                      <option value="">{t("access.anyStatus")}</option>
                      <option value="active">{t("access.active")}</option>
                      <option value="canceled">{t("access.accessCanceled")}</option>
                    </select>
                  </div>

                  <DateField
                    key={`access-history-after-${historyResetCount}`}
                    id="access-history-after"
                    label={t("access.grantedAfter")}
                    value={historyFilters.after}
                    pickerMax={historyFilters.before || undefined}
                    onChange={after => setHistoryFilters({ ...historyFilters, after })}
                    data-testid="access-history-after"
                  />

                  <DateField
                    key={`access-history-before-${historyResetCount}`}
                    id="access-history-before"
                    label={t("access.grantedBefore")}
                    value={historyFilters.before}
                    pickerMin={historyFilters.after || undefined}
                    onChange={before => setHistoryFilters({ ...historyFilters, before })}
                    data-testid="access-history-before"
                  />

                  <div className="flex items-end">
                    <Button
                      variant="outline"
                      className="w-full"
                      disabled={!hasHistoryFilters}
                      onClick={() => {
                        setHistoryFilters(EMPTY_HISTORY_FILTERS);
                        setAppliedHistoryFilters(EMPTY_HISTORY_FILTERS);
                        setHistoryResetCount(n => n + 1);
                      }}
                      data-testid="access-history-clear"
                    >
                      {t("access.clearFilters")}
                    </Button>
                  </div>
                </div>
              </Card>

              {listSection}
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("access.inviteSent")}</DialogTitle>
            <DialogDescription>{successMessage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setShowSuccessDialog(false)}>{t("access.ok")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditRolesModal} onOpenChange={setShowEditRolesModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("access.editUserRoles")}</DialogTitle>
            <DialogDescription>
              {editingAccess && (
                <>
                  {t("access.editUserRolesDesc")}{" "}
                  {editingAccess.user_name || t("access.unknownUser")} (
                  {editingAccess.user_reference_id || editingAccess.user_id})
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 max-h-[60vh] overflow-y-auto">
              {editingLockedRoles.length > 0 && (
                <div className="space-y-3 mb-3" data-testid="access-system-managed">
                  {editingLockedRoles.map(role => (
                    <Card key={role.id} className="bg-muted border-border p-4">
                      <div className="flex items-center gap-3">
                        <Badge className="bg-slate-500/10 text-slate-500">
                          {role.display_name || role.name}
                        </Badge>
                        <p className="text-sm text-muted-foreground">
                          {t("access.systemManagedRole")}
                        </p>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
              <div className="space-y-3">
                {editedRoles.map((role, index) => (
                  <Card key={index} className="bg-card border-border p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <select
                          value={role.id}
                          aria-label={t("access.selectRole")}
                          onChange={e => handleRoleChange(index, e.target.value)}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          disabled={isUpdatingRoles || isSystemManagedAccess}
                        >
                          <option value="">{t("access.selectRole")}</option>
                          {getRolesForSelect(index).map(availableRole => (
                            <option key={availableRole.id} value={availableRole.id}>
                              {availableRole.display_name || availableRole.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive/90 hover:bg-destructive/10"
                        onClick={() => handleRemoveRole(index)}
                        disabled={isUpdatingRoles || isSystemManagedAccess}
                        aria-label={t("access.removeRole")}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>

              <Button
                variant="outline"
                className="w-full mt-4 gap-2"
                onClick={handleAddRole}
                disabled={isUpdatingRoles || isSystemManagedAccess
                  || getAvailableRoles(-1).length === 0}
              >
                <Plus className="w-4 h-4" />
                {t("access.addRole")}
              </Button>
            </div>

          {editError && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive" data-testid="access-edit-error">{editError}</p>
            </div>
          )}

          {isSystemManagedAccess && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground" data-testid="access-locked-note">
                {t("access.systemManagedAccess")}
              </p>
            </div>
          )}

          {!isSystemManagedAccess && editedRoles.filter(role => role.id).length === 0 && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">{t("access.atLeastOneProfile")}</p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEditRolesModal(false);
                setEditingAccess(null);
                setEditedRoles([]);
                setEditError(null);
              }}
              disabled={isUpdatingRoles}
            >
              {t("access.cancel")}
            </Button>
            <Button
              onClick={handleUpdateRoles}
              disabled={
                isUpdatingRoles
                || isSystemManagedAccess
                || editedRoles.filter(role => role.id).length === 0
              }
              data-testid="access-edit-confirm"
            >
              {isUpdatingRoles ? t("access.updating") : t("access.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={revokingAccess !== null}
        onOpenChange={open => {
          if (!open) closeRevokeDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("access.revokeTitle")}</DialogTitle>
            <DialogDescription>
              {revokingAccess && (
                <>
                  {t("access.revokeDesc")}{" "}
                  {revokingAccess.user_name || t("access.unknownUser")} (
                  {revokingAccess.user_reference_id || revokingAccess.user_id})
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {(revokingSelf || revokingMaybeSelf) && (
            <div
              className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg"
              data-testid="access-revoke-self-warning"
            >
              <p className="text-sm text-destructive mb-3">
                {revokingSelf
                  ? t("access.revokeSelfWarning")
                  : t("access.revokeMaybeSelfWarning")}
              </p>
              <label className="flex items-center gap-2 text-sm text-destructive">
                <input
                  type="checkbox"
                  checked={confirmSelfRevoke}
                  onChange={event => setConfirmSelfRevoke(event.target.checked)}
                  data-testid="access-revoke-self-confirm"
                />
                {t("access.revokeSelfAcknowledge")}
              </label>
            </div>
          )}

          {revokeError && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive" data-testid="access-revoke-error">{revokeError}</p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeRevokeDialog}
              disabled={isRevoking}
            >
              {t("access.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleRevoke}
              disabled={isRevoking || ((revokingSelf || revokingMaybeSelf) && !confirmSelfRevoke)}
              data-testid="access-revoke-confirm"
            >
              {isRevoking ? t("access.revoking") : t("access.revokeConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};


export default AccessManagement;
